import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  List,
  Row,
  Select,
  Segmented,
  Space,
  Statistic,
  Table,
  Tag,
  Timeline,
} from 'antd';
import {
  Activity,
  Database,
  Download,
  Eye,
  FileJson,
  RefreshCw,
  Search,
  Table2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './AdminPlayerData.css';

const limit = 50;

const statusLabels = {
  online: 'Online',
  completed: 'Completed',
  idle: 'Idle',
  offline: 'Offline',
};

const formatNumber = (value) => new Intl.NumberFormat().format(value || 0);

const formatDateTime = (value) => {
  if (!value) return 'Not seen yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown time';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getTokenHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
});

const AdminPlayerData = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'org_admin' || user?.role === 'platform_admin';
  const isPlatformAdmin = user?.role === 'platform_admin';
  const [overview, setOverview] = useState(null);
  const [modules, setModules] = useState([]);
  const [players, setPlayers] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [message, setMessage] = useState('');

  const moduleNameById = useMemo(() => {
    const map = new Map();
    modules.forEach((module) => {
      map.set(module.module_id, module.title || module.module_id);
    });
    (overview?.modules || []).forEach((module) => {
      if (!map.has(module.module_id)) {
        map.set(module.module_id, module.module_id);
      }
    });
    players.forEach((player) => {
      [player.current_module_id, player.last_module_id].forEach((moduleId) => {
        if (moduleId && !map.has(moduleId)) {
          map.set(moduleId, moduleId);
        }
      });
    });
    return map;
  }, [modules, overview, players]);

  const moduleFilterOptions = useMemo(() => {
    const seen = new Set();
    const items = [];
    modules.forEach((module) => {
      if (!module.module_id || seen.has(module.module_id)) return;
      seen.add(module.module_id);
      items.push({ module_id: module.module_id, title: module.title || module.module_id });
    });
    (overview?.modules || []).forEach((module) => {
      if (!module.module_id || seen.has(module.module_id)) return;
      seen.add(module.module_id);
      items.push({ module_id: module.module_id, title: module.module_id });
    });
    players.forEach((player) => {
      [player.current_module_id, player.last_module_id].forEach((moduleId) => {
        if (!moduleId || seen.has(moduleId)) return;
        seen.add(moduleId);
        items.push({ module_id: moduleId, title: moduleId });
      });
    });
    return items;
  }, [modules, overview, players]);

  const selectedPlayer = detail?.player || players.find((item) => item.id === selectedPlayerId);
  const pageEnd = Math.min(offset + players.length, total);
  const modulePulseOptions = useMemo(() => [
    {
      value: '',
      label: (
        <span className="module-segment-label">
          <strong>All modules</strong>
          <small>{formatNumber(overview?.total_events)} events</small>
        </span>
      ),
    },
    ...(overview?.modules || []).map((module) => ({
      value: module.module_id,
      label: (
        <span className="module-segment-label">
          <strong>{moduleNameById.get(module.module_id) || module.module_id}</strong>
          <small>{formatNumber(module.players)} players · {formatNumber(module.events)} events</small>
        </span>
      ),
    })),
  ], [moduleNameById, overview]);

  const fetchModules = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/modules', {
        headers: getTokenHeaders(),
      });
      setModules(response.data || []);
    } catch (error) {
      setModules([]);
    }
  }, []);

  const fetchOverview = useCallback(async () => {
    const response = await axios.get('/api/admin/players/overview', {
      headers: getTokenHeaders(),
    });
    setOverview(response.data);
  }, []);

  const fetchPlayers = useCallback(async (nextOffset = 0, filters = {}) => {
    setLoading(true);
    setMessage('');
    try {
      const response = await axios.get('/api/admin/players', {
        headers: getTokenHeaders(),
        params: {
          q: filters.q || undefined,
          status: filters.status || undefined,
          module_id: filters.module_id || undefined,
          limit,
          offset: nextOffset,
        },
      });
      const nextPlayers = response.data.players || [];
      setPlayers(nextPlayers);
      setTotal(response.data.total || 0);
      setOffset(nextOffset);
      setSelectedPlayerId((current) => (
        current && nextPlayers.some((player) => player.id === current)
          ? current
          : nextPlayers[0]?.id || null
      ));
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to load player data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (playerId) => {
    if (!playerId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    setMessage('');
    try {
      const response = await axios.get(`/api/admin/players/${playerId}`, {
        headers: getTokenHeaders(),
        params: { limit: 40 },
      });
      setDetail(response.data);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to load player detail.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    if (isPlatformAdmin) {
      fetchModules();
    }
    fetchOverview().catch(() => setMessage('Failed to load player overview.'));
    fetchPlayers(0, { q: '', status: '', module_id: '' });
  }, [fetchModules, fetchOverview, fetchPlayers, isAdmin, isPlatformAdmin]);

  useEffect(() => {
    if (!isAdmin || !selectedPlayerId) return;
    fetchDetail(selectedPlayerId);
  }, [fetchDetail, selectedPlayerId, isAdmin]);

  const refreshAll = async () => {
    setMessage('');
    try {
      await Promise.all([
        fetchOverview(),
        fetchPlayers(offset, {
          q: query,
          status: statusFilter,
          module_id: moduleFilter,
        }),
      ]);
      if (selectedPlayerId) {
        await fetchDetail(selectedPlayerId);
      }
    } catch (error) {
      setMessage('Refresh failed.');
    }
  };

  const runSearch = async () => {
    await fetchPlayers(0, {
      q: query,
      status: statusFilter,
      module_id: moduleFilter,
    });
  };

  const applyModuleFilter = async (moduleId) => {
    setModuleFilter(moduleId);
    await fetchPlayers(0, {
      q: query,
      status: statusFilter,
      module_id: moduleId,
    });
  };

  const selectPlayer = (playerId) => {
    setSelectedPlayerId(playerId);
    setDetailDrawerOpen(true);
  };

  const exportPlayers = async (format) => {
    try {
      const response = await axios.get('/api/admin/players/export', {
        headers: getTokenHeaders(),
        params: { format },
        responseType: 'blob',
      });
      downloadBlob(response.data, `player-data.${format}`);
    } catch (error) {
      setMessage(`Failed to export player ${format.toUpperCase()}.`);
    }
  };

  const exportPlayerEvents = async (format) => {
    if (!selectedPlayerId) return;
    try {
      const response = await axios.get(`/api/admin/players/${selectedPlayerId}/events/export`, {
        headers: getTokenHeaders(),
        params: { format },
        responseType: 'blob',
      });
      const label = selectedPlayer?.display_name || selectedPlayer?.guest_session_id || `player-${selectedPlayerId}`;
      const safeLabel = String(label).replace(/[^A-Za-z0-9._-]+/g, '_');
      downloadBlob(response.data, `${safeLabel}-events.${format}`);
    } catch (error) {
      setMessage(`Failed to export player events as ${format.toUpperCase()}.`);
    }
  };

  const playerColumns = useMemo(() => [
    {
      title: 'Player',
      dataIndex: 'display_name',
      key: 'player',
      render: (_, player) => (
        <div className="player-table-cell">
          <strong>{player.display_name}</strong>
          <small>{player.email || player.guest_session_id || `Player #${player.id}`}</small>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag className={`player-status is-${status || 'offline'}`}>
          {statusLabels[status] || status || 'Unknown'}
        </Tag>
      ),
    },
    {
      title: 'Module',
      key: 'module',
      render: (_, player) => {
        const moduleId = player.current_module_id || player.last_module_id;
        return (
          <div className="player-table-cell">
            <strong>{moduleNameById.get(moduleId) || moduleId || 'No module'}</strong>
            <small>{player.last_event_type || 'No events yet'}</small>
          </div>
        );
      },
    },
    {
      title: 'Activity',
      dataIndex: 'last_seen_at',
      key: 'activity',
      render: (_, player) => (
        <div className="player-table-cell">
          <strong>{formatDateTime(player.last_seen_at)}</strong>
          <small>{formatNumber(player.total_sessions)} sessions</small>
        </div>
      ),
    },
    {
      title: 'Data',
      key: 'data',
      render: (_, player) => (
        <div className="player-table-cell">
          <strong>{formatNumber(player.total_events)} events</strong>
          <small>{formatNumber(player.completed_modules_count)} modules done</small>
        </div>
      ),
    },
  ], [moduleNameById]);

  if (!isAdmin) {
    return (
      <div className="player-data-page">
        <section className="player-data-empty">
          <Empty description="Admin access required." />
        </section>
      </div>
    );
  }

  return (
    <div className="player-data-page">
      <div className="player-data-header">
        <div>
          <span className="player-data-kicker">Player data console</span>
          <h1><Activity size={22} /> Learning Status</h1>
          <p>Track active players, module progress, xAPI-style events, and downloadable research datasets.</p>
        </div>
        <div className="player-data-header-actions">
          <Button icon={<RefreshCw size={16} />} onClick={refreshAll} loading={loading}>
            Refresh
          </Button>
          <Button icon={<FileJson size={16} />} onClick={() => exportPlayers('json')}>
            JSON
          </Button>
          <Button type="primary" icon={<Table2 size={16} />} onClick={() => exportPlayers('csv')}>
            CSV
          </Button>
        </div>
      </div>

      {message && <Alert className="player-data-message" type="error" showIcon message={message} />}

      <section className="player-data-metrics">
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} xl={6}>
            <Card className="player-metric is-total">
              <Statistic title="Total Players" value={overview?.total_players || 0} />
              <small>{formatNumber(overview?.known_players)} accounts, {formatNumber(overview?.guest_players)} guests</small>
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card className="player-metric">
              <Statistic title="Active Now" value={overview?.online_players || 0} />
              <small>Online or just completed</small>
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card className="player-metric">
              <Statistic title="Sessions" value={overview?.total_sessions || 0} />
              <small>Tracked starts</small>
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card className="player-metric">
              <Statistic title="Events" value={overview?.total_events || 0} />
              <small>{formatNumber(overview?.total_completions)} completion events</small>
            </Card>
          </Col>
        </Row>
      </section>

      <Card
        className="player-data-module-strip"
        title={<Space><Database size={17} /> Module pulse</Space>}
        extra={moduleFilter ? <Button type="link" onClick={() => applyModuleFilter('')}>All modules</Button> : null}
      >
        <div className="player-module-segmented">
          {(overview?.modules || []).length ? (
            <Segmented
              value={moduleFilter}
              options={modulePulseOptions}
              onChange={(value) => applyModuleFilter(value)}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No module activity yet." />
          )}
        </div>
      </Card>

      <div className="player-data-grid">
        <Card
          className="player-roster"
          title="Players"
          extra={<span className="player-roster-count">{total ? `${offset + 1}-${pageEnd} of ${formatNumber(total)}` : '0 players'}</span>}
        >
          <div className="player-toolbar">
            <Form className="player-toolbar-form" layout="inline" onFinish={runSearch}>
              <Form.Item className="player-search">
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') runSearch();
                  }}
                  placeholder="Search player, email, or guest id"
                  prefix={<Search size={16} />}
                />
              </Form.Item>
              <Form.Item>
                <Select
                  value={statusFilter}
                  onChange={(value) => {
                    setStatusFilter(value);
                    fetchPlayers(0, {
                      q: query,
                      status: value,
                      module_id: moduleFilter,
                    });
                  }}
                  options={[
                    { value: '', label: 'Any status' },
                    { value: 'online', label: 'Online' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'idle', label: 'Idle' },
                    { value: 'offline', label: 'Offline' },
                  ]}
                />
              </Form.Item>
              <Form.Item>
                <Select
                  value={moduleFilter}
                  onChange={applyModuleFilter}
                  options={[
                    { value: '', label: 'All modules' },
                    ...moduleFilterOptions.map((module) => ({ value: module.module_id, label: module.title })),
                  ]}
                />
              </Form.Item>
              <Form.Item>
                <Button htmlType="submit" loading={loading}>
                  Apply
                </Button>
              </Form.Item>
            </Form>
          </div>

          <Table
            className="player-roster-table"
            rowKey="id"
            columns={playerColumns}
            dataSource={players}
            loading={loading}
            scroll={{ x: 820 }}
            rowClassName={(player) => (selectedPlayerId === player.id ? 'selected' : '')}
            onRow={(player) => ({
              tabIndex: 0,
              onClick: () => selectPlayer(player.id),
              onKeyDown: (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  selectPlayer(player.id);
                }
              },
            })}
            locale={{
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No players match this view." />
            }}
            pagination={{
              current: Math.floor(offset / limit) + 1,
              pageSize: limit,
              total,
              showSizeChanger: false,
              showTotal: (count, range) => `${range[0]}-${range[1]} of ${formatNumber(count)}`,
              onChange: (page) => fetchPlayers((page - 1) * limit, {
                q: query,
                status: statusFilter,
                module_id: moduleFilter,
              }),
            }}
          />
        </Card>
      </div>

      <Drawer
        className="player-detail-drawer"
        rootClassName="player-detail-drawer"
        title={selectedPlayer ? (
          <div className="player-detail-title">
            <Tag className={`player-status is-${selectedPlayer.status || 'offline'}`}>
              {statusLabels[selectedPlayer.status] || selectedPlayer.status || 'Unknown'}
            </Tag>
            <div>
              <strong>{selectedPlayer.display_name}</strong>
              <small>{selectedPlayer.email || selectedPlayer.guest_session_id || `Player #${selectedPlayer.id}`}</small>
            </div>
          </div>
        ) : 'Player Detail'}
        open={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        width={560}
        extra={<Eye size={20} />}
        footer={selectedPlayer ? (
          <Space wrap>
            <Button type="link" icon={<FileJson size={16} />} onClick={() => exportPlayerEvents('json')}>
              Events JSON
            </Button>
            <Button type="link" icon={<Download size={16} />} onClick={() => exportPlayerEvents('csv')}>
              Events CSV
            </Button>
          </Space>
        ) : null}
      >
        {selectedPlayer ? (
          <>
            <div className="player-detail-descriptions">
              <Descriptions
                bordered
                size="small"
                column={1}
                items={[
                  { key: 'events', label: 'Events', children: formatNumber(selectedPlayer.total_events) },
                  { key: 'text', label: 'Text Inputs', children: formatNumber(selectedPlayer.text_input_events) },
                  { key: 'completed', label: 'Completed', children: formatNumber(selectedPlayer.total_completions) },
                  { key: 'first', label: 'First seen', children: formatDateTime(selectedPlayer.first_seen_at) },
                  { key: 'last', label: 'Last seen', children: formatDateTime(selectedPlayer.last_seen_at) },
                  { key: 'completion', label: 'Last completion', children: formatDateTime(selectedPlayer.last_completion_at) },
                ]}
              />
            </div>

            {detailLoading ? (
              <div className="player-data-empty small">Loading player detail...</div>
            ) : (
              <>
                <section className="player-subpanel">
                  <h3>Module Progress</h3>
                  <List
                    className="player-module-progress-list"
                    dataSource={detail?.modules || []}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No module progress recorded yet." /> }}
                    renderItem={(module) => (
                      <List.Item>
                        <List.Item.Meta
                          title={moduleNameById.get(module.module_id) || module.module_id}
                          description={`${module.last_event_type || 'No recent event'} · ${formatDateTime(module.last_active_at)}`}
                        />
                        <Space direction="vertical" align="end" size={2}>
                          <Tag className={`module-status is-${module.status}`}>{module.status}</Tag>
                          <small>{formatNumber(module.total_events)} events</small>
                        </Space>
                      </List.Item>
                    )}
                  />
                </section>

                <section className="player-subpanel">
                  <h3>Recent Event Trail</h3>
                  <div className="player-event-timeline">
                    {(detail?.recent_events || []).length ? (
                      <Timeline
                        items={(detail?.recent_events || []).slice(0, 10).map((event) => ({
                          key: event.id,
                          children: (
                            <div className="event-timeline-item">
                              <strong>{event.event_type}</strong>
                              <small>{moduleNameById.get(event.module_id) || event.module_id} · {formatDateTime(event.timestamp)}</small>
                            </div>
                          )
                        }))}
                      />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No events found for this player." />
                    )}
                  </div>
                </section>
              </>
            )}
          </>
        ) : (
          <Empty description="Select a player" />
        )}
      </Drawer>
    </div>
  );
};

export default AdminPlayerData;
